import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Send, Smile, Paperclip, X, Bot, StickyNote } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useFileManager } from '../../hooks/useFileManager';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import EmojiPicker from './EmojiPicker';
import ReplyPreview from './ReplyPreview';

export default function TeamMessageInput({
    conversationId,
    replyTo,
    onCancelReply,
    onSendMessage,
    onTyping,
    onStoppedTyping,
    insertText,
    onInsertTextConsumed,
    members,
    onShareNote
}) {
    const { theme } = useTheme();
    const [content, setContent] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [sending, setSending] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [showMentionHint, setShowMentionHint] = useState(false);
    const [mentionQuery, setMentionQuery] = useState(null);
    const [mentionIndex, setMentionIndex] = useState(0);
    const mentionStartRef = useRef(null);
    const textareaRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const typingActiveRef = useRef(false);
    const fileInputRef = useRef(null);
    const emojiTriggerRef = useRef(null);

    // Build mention candidates from conversation members (excluding bot — that's handled by @JPro hint)
    const mentionCandidates = useMemo(() => {
        return (members || [])
            .filter(m => !m.is_bot)
            .map(m => ({
                username: m.username,
                display: m.first_name ? `${m.first_name} ${m.last_name || ''}`.trim() : m.username,
                avatar_url: m.avatar_url,
            }));
    }, [members]);

    const filteredMentions = useMemo(() => {
        if (mentionQuery === null) return [];
        if (mentionQuery === '') return mentionCandidates;
        const q = mentionQuery.toLowerCase();
        return mentionCandidates.filter(
            m => m.username.toLowerCase().includes(q) || m.display.toLowerCase().includes(q)
        );
    }, [mentionQuery, mentionCandidates]);

    const insertMention = useCallback((username) => {
        const textarea = textareaRef.current;
        if (!textarea || mentionStartRef.current === null) return;
        const start = mentionStartRef.current;
        const cursorPos = textarea.selectionStart;
        const newContent = content.slice(0, start) + `@${username} ` + content.slice(cursorPos);
        setContent(newContent);
        setMentionQuery(null);
        mentionStartRef.current = null;
        setMentionIndex(0);
        setTimeout(() => {
            const newCursor = start + username.length + 2;
            textarea.selectionStart = textarea.selectionEnd = newCursor;
            textarea.focus();
        }, 0);
    }, [content]);

    const detectMention = useCallback((val, cursorPos) => {
        const textBefore = val.slice(0, cursorPos);
        // Match @word at end, but NOT if it looks like @jpro (that's the bot hint)
        const match = textBefore.match(/(^|[\s])@(\w*)$/);
        if (match) {
            const query = match[2];
            // If user is typing @jpro, let the existing JPro hint handle it
            if (/^j(p(r(o)?)?)?$/i.test(query)) {
                setMentionQuery(null);
                mentionStartRef.current = null;
                return;
            }
            mentionStartRef.current = textBefore.lastIndexOf('@');
            setMentionQuery(query);
            setMentionIndex(0);
        } else {
            setMentionQuery(null);
            mentionStartRef.current = null;
        }
    }, []);

    const { uploadedFiles, handleFileUpload, removeFile, clearFiles } = useFileManager();
    const { isDragging, dragHandlers } = useDragAndDrop(handleFileUpload);

    const adjustTextareaHeight = (textarea) => {
        textarea.style.height = 'auto';
        const newHeight = Math.min(textarea.scrollHeight, 120);
        textarea.style.height = `${newHeight}px`;
    };

    // Reset textarea height when content changes (especially when cleared)
    useEffect(() => {
        if (textareaRef.current) {
            adjustTextareaHeight(textareaRef.current);
        }
    }, [content]);

    // Focus textarea when replying
    useEffect(() => {
        if (replyTo) {
            textareaRef.current?.focus();
        }
    }, [replyTo]);

    // Autofocus textarea on mount and when switching conversations
    useEffect(() => {
        textareaRef.current?.focus();
    }, [conversationId]);

    // Insert text from AI sidebar "Use as reply"
    useEffect(() => {
        if (insertText) {
            setContent(insertText);
            onInsertTextConsumed?.();
            textareaRef.current?.focus();
        }
    }, [insertText]);

    const handleTyping = useCallback(() => {
        // Send "typing" only once per active typing burst, not on every keystroke.
        if (!typingActiveRef.current) {
            typingActiveRef.current = true;
            onTyping?.();
        }

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }

        typingTimeoutRef.current = setTimeout(() => {
            typingActiveRef.current = false;
            onStoppedTyping?.();
        }, 3000);
    }, [onTyping, onStoppedTyping]);

    const handleSend = async () => {
        const trimmed = content.trim();
        if (!trimmed && uploadedFiles.length === 0) return;

        // Clear input immediately so user sees instant feedback
        const savedContent = content;
        const savedFiles = uploadedFiles.map(f => f.data);
        const savedReplyTo = replyTo?.id;
        setContent('');
        clearFiles();
        onCancelReply?.();

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }
        typingActiveRef.current = false;
        onStoppedTyping?.();

        setSending(true);
        try {
            const ok = await onSendMessage({
                content: trimmed || '(file)',
                reply_to_id: savedReplyTo,
                files: savedFiles
            });
            // Restore input if send failed
            if (!ok) {
                setContent(savedContent);
            }
        } catch {
            setContent(savedContent);
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e) => {
        // Mention popup keyboard navigation
        if (mentionQuery !== null && filteredMentions.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setMentionIndex(prev => (prev + 1) % filteredMentions.length);
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setMentionIndex(prev => (prev - 1 + filteredMentions.length) % filteredMentions.length);
                return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                insertMention(filteredMentions[mentionIndex].username);
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setMentionQuery(null);
                mentionStartRef.current = null;
                return;
            }
        }

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleEmojiSelect = (emoji) => {
        const textarea = textareaRef.current;
        if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const newContent = content.slice(0, start) + emoji + content.slice(end);
            setContent(newContent);
            setTimeout(() => {
                textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
                textarea.focus();
            }, 0);
        } else {
            setContent(prev => prev + emoji);
        }
    };

    const canSend = !sending && (content.trim() || uploadedFiles.length > 0);

    return (
        <div
            {...dragHandlers}
            className={`relative border-t ${
                theme === 'dark' ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'
            } ${isDragging ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
        >
            <ReplyPreview message={replyTo} onCancel={onCancelReply} />

            {/* File previews */}
            {uploadedFiles.length > 0 && (
                <div className={`flex flex-wrap gap-2 px-4 pt-3 ${
                    theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                }`}>
                    {uploadedFiles.map(file => (
                        <div
                            key={file.id}
                            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-sm font-rasa border ${
                                theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                            }`}
                        >
                            {file.preview ? (
                                <img src={file.preview} alt="" className="w-8 h-8 rounded object-cover" />
                            ) : null}
                            <span className="truncate max-w-[120px]">{file.name}</span>
                            <button
                                onClick={() => removeFile(file.id)}
                                className="text-red-400 hover:text-red-500"
                            >
                                <X size={12} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Input area — styled like unified ChatInputArea */}
            <div className="p-3">
                <div className={`relative rounded-2xl border-2 transition-all duration-200 ${
                    isFocused
                        ? theme === 'dark'
                            ? 'border-blue-400 bg-gray-800'
                            : 'border-blue-500 bg-white'
                        : isDragging
                            ? theme === 'dark'
                                ? 'border-blue-400 border-dashed bg-blue-900/20'
                                : 'border-blue-500 border-dashed bg-blue-50'
                            : theme === 'dark'
                                ? 'border-gray-700 bg-gray-800 hover:border-gray-600'
                                : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                }`}>
                    {/* @JPro mention hint */}
                    {showMentionHint && (
                        <div className={`absolute -top-10 left-3 z-20`}>
                            <button
                                onClick={() => {
                                    const textarea = textareaRef.current;
                                    if (textarea) {
                                        const cursorPos = textarea.selectionStart;
                                        const textBefore = content.slice(0, cursorPos);
                                        const match = textBefore.match(/@(j(p(r(o)?)?)?)?$/i);
                                        if (match) {
                                            const start = cursorPos - match[0].length;
                                            const newContent = content.slice(0, start) + '@JPro ' + content.slice(cursorPos);
                                            setContent(newContent);
                                            setShowMentionHint(false);
                                            setTimeout(() => {
                                                textarea.selectionStart = textarea.selectionEnd = start + 7;
                                                textarea.focus();
                                            }, 0);
                                        }
                                    }
                                }}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border shadow-lg text-sm font-rasa transition-colors ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                                        : 'bg-white border-gray-200 text-gray-800 hover:bg-gray-50'
                                }`}
                            >
                                <Bot size={14} className="text-purple-400" />
                                <span className="font-unna font-semibold">@JPro</span>
                                <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>JPRO Assistant</span>
                            </button>
                        </div>
                    )}

                    {/* @user mention autocomplete */}
                    {mentionQuery !== null && filteredMentions.length > 0 && (
                        <div className={`absolute bottom-full left-3 mb-1 w-64 max-h-48 overflow-y-auto rounded-lg border shadow-lg z-20 ${
                            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}>
                            {filteredMentions.map((m, i) => (
                                <button
                                    key={m.username}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        insertMention(m.username);
                                    }}
                                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm font-rasa transition-colors ${
                                        i === mentionIndex
                                            ? theme === 'dark' ? 'bg-gray-700' : 'bg-blue-50'
                                            : theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                    }`}
                                >
                                    {m.avatar_url ? (
                                        <img src={m.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                                    ) : (
                                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-unna bg-purple-500 flex-shrink-0">
                                            {(m.display || m.username).charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <span className="font-unna font-semibold">{m.display}</span>
                                        {m.display !== m.username && (
                                            <span className={`ml-1.5 text-xs ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                            }`}>@{m.username}</span>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => handleFileUpload(e.target.files)}
                    />

                    {/* Attach button — absolute left */}
                    <div className="absolute left-3 bottom-3 flex items-center gap-0.5">
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className={`p-2 rounded-xl transition-all duration-200 group ${
                                theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                            }`}
                            title="Attach file"
                        >
                            <Paperclip size={18} className={`transition-colors duration-200 ${
                                theme === 'dark'
                                    ? 'text-gray-400 group-hover:text-blue-400'
                                    : 'text-gray-500 group-hover:text-blue-500'
                            }`} />
                        </button>
                        {onShareNote && (
                            <button
                                onClick={onShareNote}
                                className={`p-2 rounded-xl transition-all duration-200 group ${
                                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                                }`}
                                title="Share a note"
                            >
                                <StickyNote size={18} className={`transition-colors duration-200 ${
                                    theme === 'dark'
                                        ? 'text-gray-400 group-hover:text-blue-400'
                                        : 'text-gray-500 group-hover:text-blue-500'
                                }`} />
                            </button>
                        )}
                    </div>

                    {/* Textarea */}
                    <textarea
                        ref={textareaRef}
                        value={content}
                        onChange={(e) => {
                            const val = e.target.value;
                            setContent(val);
                            handleTyping();
                            adjustTextareaHeight(e.target);

                            // Detect @jpro mention hint
                            const cursorPos = e.target.selectionStart;
                            const textBeforeCursor = val.slice(0, cursorPos);
                            const mentionMatch = textBeforeCursor.match(/@(j(p(r(o)?)?)?)?$/i);
                            setShowMentionHint(!!mentionMatch);

                            // Detect @user mention autocomplete
                            detectMention(val, cursorPos);
                        }}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        placeholder="Type a message..."
                        rows={1}
                        className={`w-full pl-14 pr-24 py-3 font-rasa bg-transparent resize-none leading-relaxed transition-all duration-200 ${
                            theme === 'dark'
                                ? 'text-gray-100 placeholder-gray-400'
                                : 'text-gray-900 placeholder-gray-500'
                        } focus:outline-none min-h-[50px] max-h-[120px] overflow-auto`}
                        style={{ minHeight: '50px' }}
                        onInput={(e) => adjustTextareaHeight(e.target)}
                    />

                    {/* Right side — Emoji and Send buttons */}
                    <div className="absolute right-3 bottom-3 flex items-center gap-1">
                        <div className="relative">
                            <button
                                ref={emojiTriggerRef}
                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                className={`p-2 rounded-xl transition-all duration-200 group ${
                                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                                }`}
                                title="Emoji"
                            >
                                <Smile size={18} className={`transition-colors duration-200 ${
                                    theme === 'dark'
                                        ? 'text-gray-400 group-hover:text-blue-400'
                                        : 'text-gray-500 group-hover:text-blue-500'
                                }`} />
                            </button>
                            {showEmojiPicker && (
                                <EmojiPicker
                                    triggerRef={emojiTriggerRef}
                                    onSelect={handleEmojiSelect}
                                    onClose={() => setShowEmojiPicker(false)}
                                />
                            )}
                        </div>

                        <button
                            onClick={handleSend}
                            disabled={!canSend}
                            title="Send message"
                            className={`p-2 rounded-xl transition-all duration-200 ${
                                canSend
                                    ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95'
                                    : theme === 'dark'
                                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                        >
                            {sending ? (
                                <div className="w-[18px] h-[18px] border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Send size={18} />
                            )}
                        </button>
                    </div>
                </div>

                {/* Helper text */}
                <div className={`mt-1.5 flex items-center justify-between text-xs font-rasa ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                }`}>
                    <span>Press Enter to send, Shift+Enter for new line</span>
                </div>
            </div>
        </div>
    );
}
